import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { baseSentence } from '@fitogest/reactive-form-builder/utils';
import { BaseForm } from 'src/app/view/base-form';

import { DashboardService } from 'src/app/service/events/dashboard.service';
import { StockApiService } from 'src/app/service/api/stock-api.service';
import { FormCommonApiService } from 'src/app/service/api/formcommon-api.service';
import { FincasApiService } from 'src/app/service/api/fincas-api.service';
import { ProductosApiService } from 'src/app/service/api/productos-api';
import { VentasApiService } from 'src/app/service/api/ventas-api.service';
import { SiexApiService } from 'src/app/service/api/siex-api.service';

import { FincasModel } from 'src/app/models/form-common/fincas.module';
import { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import { StockageModel } from 'src/app/models/stock/stokage.model';
import { Compradores } from 'src/app/models/ventas/compradores.model';

import { EntradasFormDefinition, EntradasDropdownEndpoints } from './form-control/form-definition';
import {
    buildStockEntradaMirrorPayload,
    extractStockEntradaMirrorId,
} from './stock-entradas-mirror-payload.util';
import {
    buildStockEntradaCultivoParcelaSyncPayload,
    buildStockEntradaPlantasSyncPayload,
} from './stock-entradas-viverosmuzale-sync.util';
import { FormArray, Validators } from '@angular/forms';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
    FormType,
    ReactiveFormChangedField,
    ReactiveFormLifecycleEvent,
    ReactiveFormStableValueChange,
    ReactiveFormValue,
    DropdownData,
    FilteredValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { ReactiveFormPayloadSerializer } from '@fitogest/reactive-form-builder/utils';

type StockDropdownTarget = DropdownData<any> | Array<DropdownData<any>>;

type StockRequestHandler = {
    readonly toPromise?: (params?: Record<string, unknown>) => Promise<unknown>;
};

@Component({
    selector: 'app-entradas-form',
    standalone: false,
    templateUrl: './entradas-form.component.html',
    styleUrls: ['./entradas-form.component.scss']
})
export class EntradasFormComponent extends BaseForm<StockageModel> implements OnInit {
    public form!: EntradasFormDefinition;

    public loadingData = true;

    public readonly reactiveFormLoadingLabel = 'Preparando formulario de entrada de stock...';

    public isReactiveFormReady = false;

    public reactiveFormLoadingError = '';

    public initialPreviousData: StockageModel | null = null;

    public reactiveFormRecordId = '';

    private hasReversedFincasForPyf = false;

    public constructor(
        public stockApi: StockApiService,
        public formApi: FormCommonApiService,
        public fincasApi: FincasApiService,
        public productosApi: ProductosApiService,
        public ventasApi: VentasApiService,
        public siexApi: SiexApiService,
        public override route: ActivatedRoute,
        public override router: Router,
        public dashboardEvents: DashboardService,
        private http: HttpClient
    ) {
        super(
            route,
            router,
            dashboardEvents,
            stockApi.entradas.PUT,
            stockApi.entradas.POST,
            'stock-entradas',
            'Editar Entrada',
            'Crear Entrada',
            'Duplicar Entrada'
        );
    }

    public ngOnInit(): void {
        void this.initializeEntradasForm();
    }

    private async initializeEntradasForm(): Promise<void> {
        try {
            this.loadingData = true;
            this.isReactiveFormReady = false;
            this.reactiveFormLoadingError = '';
            this.resetExternalDropdowns();
            this.softInit(this.getType());
            const routeModel = this.toStockageModel(this.getModel() as unknown as Record<string, unknown>);
            this.reactiveFormRecordId = this.resolveReactiveFormRecordId(routeModel);
            const initialModel = this.initializeHostModel(routeModel);

            this.initialPreviousData = this.getInitialPreviousData(initialModel);
            this.form = new EntradasFormDefinition(
                this.getType(),
                initialModel,
                null,
                this.getDropdownEndpoints()
            );
            this.form.model = this.toStockageFormValue({
                ...this.form.model,
                ...initialModel
            });
            await this.initFormData();
            this.loadingData = false;
            this.markReactiveFormReady();
        } catch (error: unknown) {
            this.loadingData = false;
            this.reactiveFormLoadingError = this.getReadableInitialisationError(error);
            this.finalizeExternalDropdowns();
            console.error('No se ha podido preparar el formulario de entradas de stock.', error);
        }
    }

    public stableValueChanges(event: ReactiveFormStableValueChange<StockageModel>): void {
        const stableValue = this.captureStableValueChange(event.value as StockageModel);

        this.form.model = this.withRepeatableLegacyValues(stableValue);
        this.form.tag = event.changedField;
        this.syncModelFromForm();

        if (event.origin !== 'user') {
            return;
        }

        this.handleFormValueChange(event.value, event.changedField);
    }


    public onReactiveFormLifecycleEvent(event: ReactiveFormLifecycleEvent<StockageModel>): void {
        if (event.type !== 'previousDataPatched' && event.type !== 'previousDataChanged' && event.type !== 'dropdownRefresh') {
            return;
        }

        const stableValue = this.captureStableValueChange(event.value as StockageModel);

        this.form.model = this.withRepeatableLegacyValues(stableValue);
        this.form.tag = event.changedField;
        this.syncModelFromForm();

        if (event.type === 'dropdownRefresh') {
            this.postProcessLists();
            this.applySingleFincaDefault();
            return;
        }

        this.form.refreshProductosList();
        this.form.refreshLineFieldLabels();
    }

    private handleFormValueChange(
        formModel: ReactiveFormValue<StockageModel>,
        lastChangedFieldName: ReactiveFormChangedField<StockageModel>,
    ): void {
        this.form.model = this.withRepeatableLegacyValues(formModel);
        this.form.tag = lastChangedFieldName;
        if (lastChangedFieldName === 'id_comercio') {
            this.syncProveedorFromComercio();
        }

        if (lastChangedFieldName === 'tipo') {
            const isMov = String(this.form.model.tipo ?? '') === 'MOV';
            this.form.updateFincaLabel(isMov);
            this.form.updateFincaRequired(!isMov);

            const control = this.formBuilderRef?.model?.get('id_finca');
            if (control) {
                if (isMov) {
                    control.clearValidators();
                } else {
                    control.setValidators(Validators.required);
                }
                control.updateValueAndValidity();
            }
        }

        if (lastChangedFieldName === 'tipo_dato') {
            this.clearLinePayload();

            this.form.refreshProductosList();
            this.form.refreshLineFieldLabels();
            this.refreshExternalDropdownMappings();
        }

        // Cuando cambia la parcela destino en un MOV, sincronizar id_finca automáticamente
        if (lastChangedFieldName === 'ubicacion_destino' && String(this.form.model.tipo ?? '') === 'MOV') {
            this.syncFincaFromUbicacionDestino();
        }

        if (lastChangedFieldName === 'id_parcela' && this.form.env.appName === 'covidai') {
            this.syncSelectedParcel();
        }
    }

    public onAfterSend(_response: any): void {
        this.createMirrorStockExit();

        if (this.form.env.appName === 'viverosmuzale') {
            this.syncPlantasStock();
            this.syncCultivoParcelaFromStock();
        }
    }

    /**
     * Creates the synthetic stock exit that mirrors a saved MOV entry.
     */
    private createMirrorStockExit(): void {
        const rawValues = this.formBuilderRef?.model?.getRawValue() as Record<string, unknown> ?? {};
        const mirrorPayload = buildStockEntradaMirrorPayload({
            formFields: this.form.formFields,
            model: this.form.model,
            parcelas: this.form.lists.parcelas.filtered as Array<FilteredValue<ParcelasModel>>,
            rawValues,
            fallbackClienteId: this.form.cliente?.id,
            fallbackUsuarioId: this.form.usuario?.id,
        });

        if (!mirrorPayload) {
            return;
        }

        const params = new HttpParams()
            .append('p1', 'generic')
            .append('p2', 'stock_new');

        firstValueFrom(
            this.http.post<any>(`${environment.serverUrl}ws/index.php`, mirrorPayload.payload, { params })
        ).then(() => {
            console.log('espejo salida creado');
        }).catch((error: unknown) => {
            console.error('error creando espejo:', error);
        });
    }

    private syncPlantasStock(): void {
        const rawValues = this.formBuilderRef?.model?.getRawValue() as Record<string, unknown> ?? {};
        const syncPayload = buildStockEntradaPlantasSyncPayload({
            isCreation: this.getType() === FormType.CREATION,
            model: this.form.model,
            rawValues,
            parcelas: this.form.lists.parcelas.filtered as Array<FilteredValue<ParcelasModel>>,
            extractId: extractStockEntradaMirrorId,
        });

        if (!syncPayload) {
            return;
        }

        this.fincasApi.parcelasPUT.safePerform(syncPayload.payload as any);

        this.fincasApi.parcelasPUT.response(() => {
            if (!this.fincasApi.parcelasPUT.hasError) {
                syncPayload.parcela.plantas = syncPayload.plantas;
            } else {
                console.error('error actualizando plantas');
            }
        });
    }

    private syncCultivoParcelaFromStock(): void {
        const rawValues = this.formBuilderRef?.model?.getRawValue() as Record<string, unknown> ?? {};
        const syncPayload = buildStockEntradaCultivoParcelaSyncPayload({
            isCreation: this.getType() === FormType.CREATION,
            model: this.form.model,
            rawValues,
            parcelas: this.form.lists.parcelas.filtered as Array<FilteredValue<ParcelasModel>>,
            cultivos: this.form.lists.cultivosAll.filtered as Array<FilteredValue<Record<string, unknown>>>,
            extractId: extractStockEntradaMirrorId,
        });

        if (!syncPayload) {
            return;
        }

        this.fincasApi.parcelasPUT.safePerform(syncPayload.payload as any);

        this.fincasApi.parcelasPUT.response(() => {
            if (!this.fincasApi.parcelasPUT.hasError) {
                syncPayload.parcela.id_cultivo = syncPayload.idCultivo;
                syncPayload.parcela.id_variedad = syncPayload.idVariedad;
            } else {
                console.error('error actualizando cultivo/variedad de parcela');
            }
        });
    }

    private getDropdownEndpoints(): EntradasDropdownEndpoints {
        return {
            fincas: this.fincasApi.fincasGET,
            sectores: this.fincasApi.sectoresGET,
            parcelas: this.fincasApi.parcelasGET,
            comercios: this.ventasApi.compradoresGET
        };
    }

    /**
     * Loads remote data and prepares local dropdown state.
     */
    private async initFormData(): Promise<void> {
        await this.loadProductCatalogs();
        this.postProcessLists();
        this.applyInitialState();
        this.form.refreshProductosList();
        this.form.refreshLineFieldLabels();
    }

    /**
     * Loads product and crop catalogs through explicit Promise-based requests before repeatable rows are hydrated.
     */
    private async loadProductCatalogs(): Promise<void> {
        const tasks: Array<Promise<void>> = [];
        const productoParams = this.getProductoParams();

        if (!this.form.env.showStockTipoDeDato) {
            if (this.form.env.appName === 'covidai') {
                tasks.push(this.loadDropdownCatalog(
                    this.siexApi.portainjertosGET,
                    productoParams,
                    [this.form.lists.productos, this.form.lists.articulos],
                    'familia'
                ));
            } else {
                tasks.push(this.loadDropdownCatalog(
                    this.productosApi.productos.GET,
                    productoParams,
                    this.form.lists.productos,
                    'nombre'
                ));
            }
        }

        if (this.form.env.appName === 'bravosol') {
            tasks.push(this.loadDropdownCatalog(
                this.siexApi.portainjertosGET,
                productoParams,
                this.form.lists.articulos,
                'familia'
            ));
        }

        if (this.form.env.showStockTipoDeDato) {
            if (this.form.env.appName === 'covidai') {
                tasks.push(
                    this.loadDropdownCatalog(this.productosApi.cultivosPatron.GET, {}, this.form.lists.productosAll, 'nombre'),
                    this.loadDropdownCatalog(
                        this.siexApi.portainjertosGET,
                        {},
                        [this.form.lists.cultivosAll, this.form.lists.auxCultivos],
                        'descripcion'
                    )
                );
            } else {
                tasks.push(
                    this.loadDropdownCatalog(this.productosApi.productos.GET, {}, this.form.lists.productosAll, 'nombre'),
                    this.loadDropdownCatalog(
                        this.form.env.hasPlantas ? this.formApi.variedadesGET : this.siexApi.vistaCultivoVariedadGET,
                        {},
                        [this.form.lists.cultivosAll, this.form.lists.auxCultivos],
                        'cultivo'
                    )
                );
            }
        }

        await Promise.all(tasks);
    }

    private getProductoParams(): Record<string, string> {
        if (this.form.env.appName === 'vginer') {
            return { tipo: 'fertilizante' };
        }

        return {};
    }

    private async loadDropdownCatalog(
        request: unknown,
        params: Record<string, unknown>,
        target: StockDropdownTarget,
        label: string | readonly string[],
    ): Promise<void> {
        try {
            const response = await this.resolveDropdownCatalogRequest(request, params);
            const data = baseSentence(response, label as any);

            this.assignDropdownCatalog(target, data as Array<FilteredValue<unknown>>);
        } catch (error: unknown) {
            console.error('Error loading stock entradas catalog:', error);
        }
    }

    private async resolveDropdownCatalogRequest(request: unknown, params: Record<string, unknown>): Promise<unknown[]> {
        const requestHandler = request as StockRequestHandler | null;

        if (typeof requestHandler?.toPromise !== 'function') {
            return [];
        }

        const response = await requestHandler.toPromise(params);

        return Array.isArray(response) ? response : [];
    }

    private assignDropdownCatalog(target: StockDropdownTarget, data: Array<FilteredValue<unknown>>): void {
        const targets = Array.isArray(target) ? target : [target];

        for (const dropdown of targets) {
            dropdown.filtered = [...data];
            dropdown.values = [...data];
        }
    }

    /**
     * Applies local dataset transformations once remote data is available.
     */
    private postProcessLists(): void {
        if (this.form.env.appName === 'pyf' && !this.hasReversedFincasForPyf && this.form.lists.fincas.filtered.length > 0) {
            this.form.lists.fincas.filtered = [...this.form.lists.fincas.filtered].reverse();
            this.form.lists.fincas.values = [...this.form.lists.fincas.filtered];
            this.hasReversedFincasForPyf = true;
        }

        if (this.form.lists.cultivosAll.filtered.length > 0) {
            this.form.lists.cultivosAll.filtered = this.form.lists.cultivosAll.filtered.filter((item, index, array) => {
                if (index === 0) {
                    return true;
                }

                return array[index - 1]?.label !== item.label;
            });

            this.form.lists.cultivosAll.values = [...this.form.lists.cultivosAll.filtered];
        }

        this.form.refreshProductosList();
        this.form.refreshLineFieldLabels();
    }

    /**
     * Applies startup defaults after dropdowns are loaded.
     */
    private applyInitialState(): void {
        this.applySingleFincaDefault();

        if (this.form.env.appName === 'ava' && !this.form.model.tipo) {
            this.form.model.tipo = 'E';
        }

        if (this.form.env.forcesPlantType && !this.form.model.tipo_dato) {
            this.form.model.tipo_dato = 'Semilla';
        }

        this.syncProveedorFromComercio();

        if (this.form.env.appName === 'covidai' && this.getType() !== FormType.CREATION) {
            this.syncSelectedParcel();
        }
    }

    /**
     * Preserves the legacy single-finca default after fincas move to a declarative data source.
     */
    private applySingleFincaDefault(): void {
        if (this.getType() !== FormType.CREATION || this.hasNonEmptyValue(this.form.model.id_finca)) {
            return;
        }

        const availableFincas = this.form.lists.fincas.filtered.filter((item) => item.value !== null);

        if (availableFincas.length !== 1) {
            return;
        }

        const finca = availableFincas[0]?.value as FincasModel | null;
        const fincaId = String(finca?.id ?? '').trim();

        if (fincaId === '') {
            return;
        }

        this.form.model.id_finca = fincaId;
        this.formBuilderRef?.model?.get('id_finca')?.setValue(fincaId, { emitEvent: true });
    }

    /**
     * Builds a local legacy view from repeatable FormArray values without syncing hidden controls.
     */
    private withRepeatableLegacyValues(formModel: ReactiveFormValue<StockageModel>): ReactiveFormValue<StockageModel> {
        const legacyValues = ReactiveFormPayloadSerializer.serializeRepeatableLegacyFields(
            this.form.formFields,
            formModel as Record<string, unknown>
        );

        return { ...formModel, ...legacyValues } as ReactiveFormValue<StockageModel>;
    }

    /**
     * Refreshes builder mappings after local option lists or labels are replaced.
     */
    private refreshExternalDropdownMappings(): void {
        this.formBuilderRef?.requestExternalDropdownMappingsRefresh();
    }

    /**
     * Syncs the provider text field with the selected company.
     */
    private syncProveedorFromComercio(): void {
        const selected = this.resolveSelectedDropdownEntity<Compradores>('id_comercio', this.form.lists.comercios);

        if (selected?.razon_social != null) {
            this.form.model.proveedor = selected.razon_social;
        }
    }

    /**
     * Clears serialized line payload fields when the product type changes.
     */
    private clearLinePayload(): void {
        this.form.model.ids_productos = '';
        this.form.model.ids_tipos = '';
        this.form.model.array_unidades = '';
        this.form.model.array_lotes = '';
        this.form.model.array_marcas = '';
        this.form.model.array_envases = '';
        this.form.model.array_lotes_conselleria = '';
        this.form.model.unidades_medida = '';
        this.form.model.id_variedad = '';
        this.form.model.tipo_semilla = '';

        const lineasGroup = this.formBuilderRef?.model?.get('lineas_group');

        if (!(lineasGroup instanceof FormArray)) {
            return;
        }

        lineasGroup.controls.forEach((rowControl) => rowControl.reset({}, { emitEvent: false }));
        lineasGroup.updateValueAndValidity({ emitEvent: true });
    }

    /**
     * Keeps parcel controls aligned in Covidai edition flows without mutating DropdownData.selected.
     */
    private syncSelectedParcel(): void {
        const ubicacion = String(this.form.model.ubicacion ?? '');

        if (ubicacion === '') {
            return;
        }

        const selected = this.form.lists.parcelas.filtered.find((parcelaItem) => {
            const parcela = parcelaItem.value as ParcelasModel | null;

            return String(parcela?.id ?? '') === ubicacion;
        });
        const selectedId = String((selected?.value as ParcelasModel | null)?.id ?? ubicacion);

        this.form.model.ubicacion = selectedId;
        this.formBuilderRef?.model?.get('ubicacion')?.setValue(selectedId, { emitEvent: false });
        this.formBuilderRef?.model?.get('id_parcela')?.setValue(selectedId, { emitEvent: false });
    }

    private resolveSelectedDropdownEntity<T extends object>(fieldName: string, dropdown: DropdownData<T>): T | null {
        const currentValue = this.formBuilderRef?.model?.get(fieldName)?.value
            ?? (this.form.model as Record<string, unknown>)[fieldName];

        if (this.isRecord(currentValue)) {
            return currentValue as T;
        }

        const tokens = this.getReactiveValueTokens(currentValue);

        if (tokens.length === 0) {
            return null;
        }

        const options = [...dropdown.values, ...dropdown.filtered];

        for (const option of options) {
            const entity = this.getDropdownOptionEntity<T>(option);

            if (entity === null) {
                continue;
            }

            if (this.dropdownOptionMatchesValue(option, entity, tokens)) {
                return entity;
            }
        }

        return null;
    }

    private getReactiveValueTokens(value: unknown): string[] {
        if (Array.isArray(value)) {
            return value.flatMap((item) => this.getReactiveValueTokens(item));
        }

        if (this.isRecord(value)) {
            return ['id', 'codigo', 'value']
                .map((key) => String(value[key] ?? '').trim())
                .filter((token) => token.length > 0);
        }

        return String(value ?? '')
            .split(';')
            .map((token) => token.trim())
            .filter((token) => token.length > 0);
    }

    private dropdownOptionMatchesValue<T extends object>(
        option: { readonly value?: unknown; readonly raw?: unknown; readonly label?: string },
        entity: T,
        tokens: string[]
    ): boolean {
        const optionTokens = [
            option.value,
            option.raw,
            option.label,
            this.asRecord(entity)['id'],
            this.asRecord(entity)['codigo'],
            this.asRecord(entity)['referencia'],
            this.asRecord(entity)['razon_social'],
            this.asRecord(entity)['nombre']
        ]
            .flatMap((value) => this.getReactiveValueTokens(value))
            .filter((token, index, values) => values.indexOf(token) === index);

        return tokens.some((token) => optionTokens.includes(token));
    }

    private getDropdownOptionEntity<T extends object>(
        option: { readonly value?: unknown; readonly raw?: unknown }
    ): T | null {
        if (this.isRecord(option.raw)) {
            return option.raw as T;
        }

        if (this.isRecord(option.value)) {
            return option.value as T;
        }

        return null;
    }

    private asRecord(value: object): Record<string, unknown> {
        return value as Record<string, unknown>;
    }

    private isRecord(value: unknown): value is Record<string, unknown> {
        return typeof value === 'object' && value !== null;
    }

    private hasNonEmptyValue(value: unknown): boolean {
        return value !== null && value !== undefined && String(value).trim() !== '';
    }


    private markReactiveFormReady(): void {
        this.isReactiveFormReady = true;
        this.finalizeExternalDropdowns();
        this.refreshExternalDropdownMappings();
    }

    private syncModelFromForm(): void {
        const normalizedModel = this.toStockageModel(this.form.model);

        this.form.model = this.toStockageFormValue(normalizedModel);
        this.updateHostModel(normalizedModel);
    }

    private getInitialPreviousData(routeModel: StockageModel): StockageModel | null {
        return Object.keys(routeModel as Record<string, unknown>).length === 0
            ? null
            : this.toStockageModel(routeModel as unknown as Record<string, unknown>);
    }

    private getReadableInitialisationError(error: unknown): string {
        if (error instanceof Error && error.message.trim() !== '') {
            return `No se ha podido cargar el formulario de entrada de stock: ${error.message}`;
        }

        return 'No se ha podido cargar el formulario de entrada de stock. Revisa la conexión y vuelve a intentarlo.';
    }

    private toStockageFormValue(value: Record<string, unknown> | StockageModel): ReactiveFormValue<StockageModel> {
        return { ...(value as Record<string, unknown>) } as ReactiveFormValue<StockageModel>;
    }

    private toStockageModel(value: Record<string, unknown> | ReactiveFormValue<StockageModel> | StockageModel): StockageModel {
        return { ...(value as Record<string, unknown>) } as unknown as StockageModel;
    }



    private syncFincaFromUbicacionDestino(): void {
        const extractId = (val: unknown): string => {
            if (val == null) return '';
            if (typeof val === 'object') return String((val as Record<string, unknown>)['id'] ?? '');
            return String(val);
        };

        const ubicacionDestinoId = extractId(this.form.model.ubicacion_destino);

        if (ubicacionDestinoId === '') {
            return;
        }

        const found = this.form.lists.parcelas.filtered.find(
            (item) => String((item.value as ParcelasModel)?.id ?? '') === ubicacionDestinoId
        );

        const idFincaDestino = String((found?.value as ParcelasModel)?.id_finca ?? '');

        if (idFincaDestino !== '') {
            this.form.model.id_finca = idFincaDestino;

            const control = this.formBuilderRef?.model?.get('id_finca');
            if (control) {
                control.setValue(idFincaDestino, { emitEvent: false });
            }
        }
    }
}
